/** Scope 1 and Scope 2 as table pages - ADR-0212 decisions 2, 5 and 10.
 *
 * A title card with Import and Add, a strip of tiles, one filter line and one bordered grid. The
 * three tabs the route table draws (Figures, By month, Data quality) are three views of one place;
 * `?tab=` says which. **The text diet holds here**: no sentence of the old screens is on the page,
 * every one of them is behind an info control (`infoText.ts`, sections `scope1` and `scope2`).
 *
 * Rules that survived from the screens this replaces, each of which has a test:
 *
 * - **A tile is a claim, so an unread one is a dash.** A failed or still-loading read never renders
 *   as `0.0 tCO2e` or as "every figure metered" (EXP-29).
 * - **Every total is the server's** (`GET /inventory`). The grid's rows are figures; nothing here adds
 *   them up, and a tile that opens a breakdown opens the drawer of the same cut the server summed.
 * - **Scope 2 shows one number.** The market-based column and tile appear when an instrument is
 *   recorded in the period (the market inventory says so) or when the Advanced view is on
 *   (`?method=market`, which My settings' Advanced switch sets). The data is calculated either way and
 *   a receipt always names its method (rule 3); this chooses only what the table puts in front of
 *   the reader (ADR-0212 decision 5).
 */

import { Flame, Lightning } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { ReactNode } from "react";

import { useMe } from "../../api/auth";
import type { FigureCut, Inventory } from "../../api/queries";
import { useFiguresPaged, useInventory } from "../../api/queries";
import { useCoverage } from "../../api/sources";
import { useOrgTree } from "../../api/vendorSummary";
import { EngagementWindowNote } from "../../components/EngagementWindowNote";
import { ReadFailed } from "../../components/Loaded";
import { WindowLockBanner } from "../../components/WindowLockBanner";
import {
  ActionButton,
  AddImportActions,
  DashboardBand,
  EmptyAction,
  EmptyState,
  TablePage,
  Tile,
  TileStrip,
} from "../../components/ui";
import { windowUnderEngagement } from "../../lib/engagement";
import type { WindowUnderEngagement } from "../../lib/engagement";
import { estimatesAmong } from "../../lib/estimate";
import { quantity, tonnes } from "../../lib/format";
import { RequireSession } from "../../shell/RequireSession";
import { mayEnterData } from "../../shell/routeTable";
import { BandFailures } from "../bands/BandFailures";
import { CategoryShareCard, SiteShareCard } from "../bands/ShareCards";
import { MonthlyCard } from "../bands/MonthlyCard";
import { useEmissionsTrend } from "../bands/useEmissionsTrend";
import { monthOfBreakdown, useMonthDrawer } from "../bands/monthBreakdown";
import { useOpenMonth } from "../bands/useOpenMonth";
import { pagingOf, WHOLE_RECORDS_NOTE } from "../drawerFigures";
import { RequirePeriod } from "../NoPeriod";
import { ScreenFrame } from "../ScreenFrame";
import { keepSearch } from "../search";
import { useUrlTab } from "../useUrlTab";
import type { Screen, Window as ScreenWindow } from "../useScreen";
import { DASH } from "./context";
import type { ScopeContext } from "./context";
import { AddFigureDrawer } from "./AddFigureDrawer";
import { CertificatesDrawer } from "./CertificatesDrawer";
import { useFiguresTable } from "./FiguresTable";
import { MonthView } from "./MonthView";
import { QualityView } from "./QualityView";
import { Scope2Basis } from "./Scope2Basis";
import { TIER_WORD, scopeOfCategory, words } from "./model";

const TABS = [
  { key: "figures", label: "Figures" },
  { key: "month", label: "By month" },
  { key: "quality", label: "Data quality" },
] as const;

/** Spelled as `infoKey: "..."` so `infoText.test.ts`, which reads the source for the keys a component
 * names, finds both. */
const HOW_INFO = {
  "1": { infoKey: "scope1.how-calculated" },
  "2": { infoKey: "scope2.how-calculated" },
} as const;

function ScopePageBody({
  scope,
  screen,
  period,
  engagement,
}: {
  scope: "1" | "2";
  screen: Screen;
  period: ScreenWindow;
  engagement: WindowUnderEngagement;
}) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const role = me?.role;
  const [tab] = useUrlTab(TABS);
  const [adding, setAdding] = useState(false);
  const [certificates, setCertificates] = useState(false);

  // Scope 1 has one basis; Scope 2 reads both inventories (the market one says whether an
  // instrument is recorded), and the table shows the second only when it should.
  const locationRead = useInventory(period, scope === "2" ? "location" : screen.method);
  const marketRead = useInventory(scope === "2" ? period : undefined, "market");
  const coverage = useCoverage(period.period_start, period.period_end);
  // The band's reads: the months and the census (bars, the tile's line and change) and the
  // organisation tree (the sites). Scope 2's band is on the location basis, as its table is.
  // **The band follows the basis in force** (Q-04): with `?method=market` the headline tile is the
  // market-based figure, and a change against the prior period, the months and the sites beneath it
  // are the same basis - not a location-based chart under a market-based number.
  const trend = useEmissionsTrend(screen, screen.method, [scope], scope);
  const tree = useOrgTree(period.period_start, period.period_end, screen.method);
  const openMonth = useOpenMonth(screen);
  const failed =
    locationRead.error ?? (scope === "2" ? marketRead.error : null) ?? null;
  const unread =
    Boolean(failed) || locationRead.isPending || (scope === "2" && marketRead.isPending);
  // A read still out, as against one that failed: a grey bar in the tiles, where a failure keeps its dash.
  const answering = !failed && (locationRead.isLoading || (scope === "2" && marketRead.isLoading));

  const instrumentRecorded = (marketRead.data?.scope2?.covered_figures ?? 0) > 0;
  const showMarket = scope === "2" && (screen.method === "market" || instrumentRecorded);

  const card = locationRead.data?.totals_by_scope.find((c) => c.scope === scope);
  const marketCard = marketRead.data?.totals_by_scope.find((c) => c.scope === "2");
  const figureCount = card?.figures ?? 0;
  const estimated = estimatesAmong(figureCount, card?.graded_a ?? 0);

  const categories = (locationRead.data?.by_category ?? [])
    .filter((c) => c.scope === scope)
    .sort((a, b) => Number(b.kgco2e) - Number(a.kgco2e));
  const tiers = [...(marketRead.data?.scope2?.by_tier ?? [])].sort((a, b) => a.tier.localeCompare(b.tier));

  // The census names a category and no scope; its windows are placed by the engine's own sets.
  const missing = coverage.data
    ? coverage.data.sources
        .filter((source) => scopeOfCategory(source.category) === scope)
        .reduce((sum, source) => sum + source.missing, 0)
    : undefined;

  // ---- the breakdown the receipt drawer opens on a tile (the same cut the server summed)
  const key = screen.breakdown;
  const isCategory = scope === "1" && categories.some((c) => c.category === key);
  const tier = scope === "2" ? tiers.find((t) => t.tier === key) : undefined;
  // A site in the band's by-site list: the figures at that site, in this scope.
  const siteId = /^site-([0-9a-f-]{36})$/.exec(key ?? "")?.[1];
  const site = siteId ? tree.data?.facilities.find((f) => f.facility_id === siteId) : undefined;
  // A market-based cut is one the market inventory states: the total itself, an instrument tier, or
  // the load an instrument did or did not reach. Everything else is the location-based inventory.
  const onMarket = key === "market" || key === "covered" || key === "fallback" || tier !== undefined;
  const cut: FigureCut = {
    scope,
    ...(key === "estimated" ? { grade: "below_a" as const } : {}),
    ...(key === "grade-a" ? { grade: "A" as const } : {}),
    ...(isCategory && key ? { category: key } : {}),
    ...(key === "covered" ? { coverage: "covered" as const } : {}),
    ...(key === "fallback" ? { coverage: "fallback" as const } : {}),
    ...(tier ? { marketTier: tier.tier } : {}),
    ...(site ? { facilityIds: [site.facility_id] } : {}),
  };
  // A month in the band's bars opens the same drawer on that month, in this scope only.
  const monthDrawer = useMonthDrawer({
    key,
    window: period,
    method: screen.method,
    scope,
    months: trend.monthly.data?.months,
    label: `Scope ${scope}`,
  });
  const breakdownRead = useFiguresPaged(
    key && !monthOfBreakdown(key) ? period : undefined,
    onMarket ? "market" : key === "location" ? "location" : screen.method,
    cut,
  );
  /** The server's total for exactly this selection, where it states one. A grade or a coverage has no
   * server total, so its drawer prints none for a partial list rather than adding up the part. */
  const headline =
    key === "market"
      ? marketCard?.assessed
        ? marketCard.kgco2e
        : undefined
      : tier
        ? tier.kgco2e
        : isCategory
          ? categories.find((c) => c.category === key)?.kgco2e
          : site
            ? site.totals.by_scope[Number(scope) - 1]?.kgco2e
            : key === "scope-1" || key === "location"
            ? card?.assessed
              ? card.kgco2e
              : undefined
            : undefined;
  const label = site
    ? `Scope ${scope} · ${site.name}`
    : key === "estimated"
      ? `Scope ${scope} · below grade A`
      : key === "grade-a"
        ? `Scope ${scope} · grade A`
        : key === "covered"
          ? "Covered by an instrument"
          : key === "fallback"
            ? "Fell back to the location factor"
            : tier
              ? (TIER_WORD[tier.tier] ?? tier.tier)
              : key === "market"
                ? "Scope 2 · market-based"
                : isCategory
                  ? `Scope 1 · ${words(key ?? "")}`
                  : scope === "2"
                    ? "Scope 2 · location-based"
                    : "Scope 1";

  const retry = () => {
    void locationRead.refetch();
    if (scope === "2") void marketRead.refetch();
  };

  const ctx: ScopeContext = {
    scope,
    screen,
    period,
    role,
    missing,
    unread,
    failed,
    answering,
    retry,
    coverageError: coverage.error,
    retryCoverage: () => void coverage.refetch(),
    marketPending: scope === "2" && marketRead.isPending,
    engagement,
  };
  const title = `Scope ${scope}`;

  return (
    <ScreenFrame
      screen={screen}
      title={title}
      crumb={scope === "1" ? "Direct emissions" : "Purchased electricity"}
      {...(monthDrawer ?? {
        breakdownFigures: breakdownRead.figures,
        breakdownLabel: label,
        breakdownRead,
        breakdownPaging: pagingOf(breakdownRead),
        breakdownHeadline: headline,
        breakdownNote: headline !== undefined ? WHOLE_RECORDS_NOTE : undefined,
      })}
    >
      {tab === "figures" ? (
        <FiguresView
          ctx={ctx}
          card={card}
          estimated={estimated}
          categories={categories}
          marketTotal={marketCard?.assessed ? marketCard.kgco2e : undefined}
          basis={screen.method}
          showMarket={showMarket}
          totalKwh={locationRead.data?.scope2?.total_kwh}
          coveredKwh={marketRead.data?.scope2?.covered_kwh}
          coveredFigures={marketRead.data?.scope2?.covered_figures}
          band={{ trend, tree, inventory: locationRead, openMonth }}
          onAdd={() => setAdding(true)}
          onImport={() => void navigate({ to: "/bulk", search: keepSearch })}
          onCertificates={() => setCertificates(true)}
        />
      ) : tab === "month" ? (
        <MonthView ctx={ctx} />
      ) : (
        <QualityView ctx={ctx} />
      )}
      <AddFigureDrawer scope={scope} open={adding} onClose={() => setAdding(false)} />
      {scope === "2" ? (
        <CertificatesDrawer open={certificates} onClose={() => setCertificates(false)} role={role} />
      ) : null}
    </ScreenFrame>
  );
}

// ------------------------------------------------------------------------------------ Figures

function FiguresView({
  ctx,
  card,
  estimated,
  categories,
  marketTotal,
  basis,
  showMarket,
  totalKwh,
  coveredKwh,
  coveredFigures,
  band,
  onAdd,
  onImport,
  onCertificates,
}: {
  ctx: ScopeContext;
  card: Inventory["totals_by_scope"][number] | undefined;
  estimated: number;
  categories: Inventory["by_category"];
  marketTotal: string | undefined;
  /** The Scope 2 basis in force (`?method`): which of the two totals the first tile headlines. */
  basis: "location" | "market";
  showMarket: boolean;
  totalKwh: string | undefined;
  coveredKwh: string | undefined;
  coveredFigures: number | undefined;
  band: {
    trend: ReturnType<typeof useEmissionsTrend>;
    tree: ReturnType<typeof useOrgTree>;
    inventory: ReturnType<typeof useInventory>;
    openMonth: (month: string) => void;
  };
  onAdd: () => void;
  onImport: () => void;
  onCertificates: () => void;
}) {
  const {
    scope,
    screen,
    period,
    role,
    missing,
    unread,
    failed,
    answering,
    retry,
    coverageError,
    retryCoverage,
    marketPending,
  } = ctx;
  const navigate = useNavigate();

  const total = card?.assessed ? card.kgco2e : "0";
  const largest = categories[0];
  const gradeA = card?.graded_a ?? 0;
  const count = card?.figures ?? 0;
  // No figure yet is a dash, as an unread tile is: "0.0 tCO2e" and "0 of 0" read as measured results
  // for a scope nobody has recorded anything in (KD, 2026-11-02).
  const dash = unread || count === 0;

  // **The headline tile names its basis** (G-Wviewer-05, G-Wapprover-24). Scope 1 has one and says
  // "Scope 1 total"; Scope 2 is calculated two ways, so its first tile is the location-based or the
  // market-based total as `?method` says, and the other basis, where shown, is its own tile beside it.
  // The two are never added.
  const marketHeadline = scope === "2" && basis === "market";
  const headlineKg = marketHeadline ? marketTotal : card?.assessed ? card.kgco2e : "0";
  const headlineDash = marketHeadline ? dash || marketTotal === undefined : dash;
  const headlineWord = marketHeadline ? "market-based" : "location-based";
  const tiles: ReactNode[] = [
    <Tile
      key="total"
      loading={answering}
      label={scope === "1" ? "Scope 1 total" : marketHeadline ? "Market-based total" : "Location-based total"}
      value={headlineDash ? DASH : tonnes(headlineKg ?? "0")}
      unit={headlineDash ? undefined : "tCO₂e"}
      {...(marketHeadline ? { infoKey: "scope2.market-based" as const } : HOW_INFO[scope])}
      onOpen={
        headlineDash
          ? undefined
          : () => screen.openBreakdown(scope === "1" ? "scope-1" : marketHeadline ? "market" : "location")
      }
      {...(!headlineDash && band.trend.delta ? { delta: band.trend.delta } : {})}
      openLabel={
        scope === "1"
          ? `Scope 1 total: ${tonnes(total)} tonnes. Show the figures behind it`
          : `Scope 2 total: ${tonnes(headlineKg ?? "0")} tonnes, ${headlineWord}. Show the figures behind it`
      }
      testId={`tile-total-${scope}`}
    />,
  ];
  if (scope === "2" && showMarket && !marketHeadline) {
    tiles.push(
      <Tile
        key="market"
        loading={answering}
        label="Market-based total"
        value={dash || marketTotal === undefined ? DASH : tonnes(marketTotal)}
        unit={dash || marketTotal === undefined ? undefined : "tCO₂e"}
        infoKey="scope2.market-based"
        onOpen={dash || marketTotal === undefined ? undefined : () => screen.openBreakdown("market")}
        openLabel={`Market-based Scope 2: ${marketTotal === undefined ? "" : tonnes(marketTotal)} tonnes. Show the figures behind it`}
        testId="tile-market"
      />,
    );
  }
  if (scope === "2" && marketHeadline) {
    tiles.push(
      <Tile
        key="location"
        loading={answering}
        label="Location-based total"
        value={dash ? DASH : tonnes(total)}
        unit={dash ? undefined : "tCO₂e"}
        {...HOW_INFO[scope]}
        onOpen={dash ? undefined : () => screen.openBreakdown("location")}
        openLabel={`Location-based Scope 2: ${tonnes(total)} tonnes. Show the figures behind it`}
        testId="tile-location"
      />,
    );
  }
  if (scope === "1") {
    tiles.push(
      <Tile
        key="largest"
        loading={answering}
        label="Largest source"
        value={dash || !largest ? DASH : words(largest.category)}
        onOpen={dash || !largest ? undefined : () => screen.openBreakdown(largest.category)}
        testId="tile-largest"
      />,
    );
  } else if (showMarket) {
    tiles.push(
      <Tile
        key="covered"
        loading={answering}
        label="Covered by instruments"
        value={dash || coveredKwh === undefined ? DASH : quantity(coveredKwh)}
        unit={dash || coveredKwh === undefined ? undefined : "kWh"}
        onOpen={dash || !coveredFigures ? undefined : () => screen.openBreakdown("covered")}
        openLabel={`Load covered by an instrument: ${coveredKwh === undefined ? "" : quantity(coveredKwh)} kWh. Show the figures behind it`}
        testId="tile-covered"
      />,
    );
  } else {
    tiles.push(
      <Tile
        key="kwh"
        loading={answering}
        label="Electricity used"
        value={dash || totalKwh === undefined ? DASH : quantity(totalKwh)}
        unit={dash || totalKwh === undefined ? undefined : "kWh"}
        onOpen={dash || totalKwh === undefined ? undefined : () => screen.openBreakdown("location")}
        openLabel={`Electricity used: ${totalKwh === undefined ? "" : quantity(totalKwh)} kWh. Show the figures behind it`}
        testId="tile-kwh"
      />,
    );
  }
  tiles.push(
    <Tile
      key="estimates"
      loading={answering}
      label="Estimates"
      value={dash ? DASH : String(estimated)}
      infoKey="scope1.grades"
      onOpen={dash || estimated === 0 ? undefined : () => screen.openBreakdown("estimated")}
      openLabel={`${String(estimated)} Scope ${scope} figures below grade A. Show them`}
      testId="tile-estimates"
    />,
  );
  if (scope === "1") {
    tiles.push(
      <Tile
        key="quality"
        loading={answering}
        label="Grade A figures"
        value={dash ? DASH : `${String(gradeA)} of ${String(count)}`}
        onOpen={dash || gradeA === 0 ? undefined : () => screen.openBreakdown("grade-a")}
        openLabel={`${String(gradeA)} of ${String(count)} Scope 1 figures are grade A. Show them`}
        testId="tile-quality"
      />,
    );
  }
  tiles.push(
    <Tile
      key="missing"
      loading={band.trend.coverage.isLoading}
      label="Missing bills"
      value={missing === undefined || (count === 0 && missing === 0) ? DASH : String(missing)}
      infoKey="scope1.missing"
      testId="tile-missing"
    />,
  );

  const grid = useFiguresTable({
    scope,
    window: period,
    method: "location",
    // Scope 2's market column depends on the market inventory (an instrument may add one), so the grid
    // waits for it rather than appearing with one column and growing a second.
    loading: marketPending,
    serverTotalKg: card?.assessed ? card.kgco2e : undefined,
    showMarket,
    carrierLabel: scope === "1" ? "Fuel" : "Carrier",
    onReceipt: screen.openReceipt,
    onOpenRecord: (id) =>
      void navigate({ to: "/records/$recordId", params: { recordId: id }, search: keepSearch }),
    emptyTitle: `No Scope ${scope} figures yet`,
    emptyBody: scope === "1" ? "Fuel and refrigerant bills land here." : "Electricity bills land here.",
    emptyIcon: scope === "1" ? Flame : Lightning,
    emptyAction: (
      <EmptyAction
        label="Upload a bill"
        allowed={role === undefined ? undefined : mayEnterData(role)}
        reason="A Contributor, Approver or Admin uploads bills."
        to="/upload"
        search={keepSearch}
        plus={false}
        testId="scope-empty-upload"
      />
    ),
  });
  // A read that failed is said in the table's place: the grid, and the filter line over it, are for
  // rows the page has.
  const failure = failed ? (
    <ReadFailed of={`The Scope ${scope} figures`} error={failed} onRetry={retry} />
  ) : grid.read.error ? (
    <ReadFailed
      of={`The Scope ${scope} figures`}
      error={grid.read.error}
      onRetry={grid.read.refetch}
    />
  ) : grid.facetsError ? (
    <ReadFailed
      of={`The Scope ${scope} filters`}
      error={grid.facetsError}
      onRetry={grid.refetchFacets}
    />
  ) : undefined;

  return (
    <TablePage
      icon={scope === "1" ? Flame : Lightning}
      title={`Scope ${scope}`}
      actions={
        <>
          {scope === "2" ? <Scope2Basis screen={screen} /> : null}
          {scope === "2" ? (
            <ActionButton data-testid="open-certificates" onClick={onCertificates}>
              Certificates
            </ActionButton>
          ) : null}
          <AddImportActions role={role} onAdd={onAdd} onImport={onImport} />
        </>
      }
      band={
        <>
          {/* What the engagement did to this window, for an Auditor whose window it narrowed (Q-03). */}
          <EngagementWindowNote under={ctx.engagement} />
          {/* Whether the window these figures are for is closed (ADR-0094): nothing for an open one. */}
          <WindowLockBanner lock={band.inventory.data?.window_lock} />
          {coverageError ? (
            <ReadFailed of="The expected bills" error={coverageError} onRetry={retryCoverage} />
          ) : null}
          <BandFailures
            reads={[
              { of: "The monthly figures", read: band.trend.monthly },
              { of: "The comparison with the prior period", read: band.trend.trends },
              { of: "The sites", read: band.tree },
            ]}
          />
          <DashboardBand
            label={`Scope ${scope} dashboard`}
            tiles={<TileStrip label={`Scope ${scope} summary`}>{tiles}</TileStrip>}
            charts={[
              <MonthlyCard
                key="months"
                scopes={[scope]}
                bars={band.trend.bars}
                reads={band.trend.reads}
                onOpenMonth={band.openMonth}
                unreconciled={band.trend.unreconciled}
              />,
              ...(scope === "1"
                ? [
                    <CategoryShareCard
                      key="categories"
                      read={band.inventory}
                      inventory={band.inventory.data}
                      scope="1"
                      onOpen={(category) => screen.openBreakdown(category)}
                    />,
                  ]
                : []),
              <SiteShareCard
                key="sites"
                read={band.tree}
                tree={band.tree.data}
                scope={scope}
                onOpen={(key) => screen.openBreakdown(key)}
              />,
            ]}
          />
        </>
      }
      // **Nothing to chart** is a window the inventory answered with no figures - or one whose totals
      // could not be read at all. A band of charts over a failed read drew its "No figures in this
      // period" cards beside the failure notice, so a read that failed claimed the period was empty
      // (G-P9-13, -14); and while the inventory is still out the cards are skeletons, never that claim.
      nothingYet={Boolean(failed) || (!unread && count === 0)}
      filters={failure ? undefined : grid.filters}
      table={failure ?? grid.table}
    />
  );
}

/** **A window the engagement does not reach has nothing in it to show** (Q-03, G-Wauditor-05). An
 * Auditor who sets the address to a year they are not engaged for was shown a red *"the expected bills
 * could not be loaded"* (the census's 404, by design), dashes for tiles and an empty state offering
 * "a Contributor uploads bills" to a role that cannot upload: three ways of saying the product had
 * failed, about a request it answered correctly. This says what happened, once, and asks for nothing -
 * the inventory, the census and the months are not read at all.
 *
 * Decided from the person's own terms (`/auth/me`), not from a refusal: nothing the server withheld
 * is described, and a window that straddles the edge is not here but in the page, as a note. */
function OutsideEngagementView({ scope, under }: { scope: "1" | "2"; under: WindowUnderEngagement }) {
  return (
    <TablePage
      icon={scope === "1" ? Flame : Lightning}
      title={`Scope ${scope}`}
      state={
        <EmptyState title={`No Scope ${scope} figures in your engagement`} testId="scope-outside-engagement">
          <EngagementWindowNote under={under} />
        </EmptyState>
      }
    />
  );
}

function ScopeUnderEngagement({
  scope,
  screen,
  period,
}: {
  scope: "1" | "2";
  screen: Screen;
  period: ScreenWindow;
}) {
  const { data: me } = useMe();
  const under = windowUnderEngagement(me, period.period_start, period.period_end);
  if (under?.kind === "outside") {
    return (
      <ScreenFrame
        screen={screen}
        title={`Scope ${scope}`}
        crumb={scope === "1" ? "Direct emissions" : "Purchased electricity"}
      >
        <OutsideEngagementView scope={scope} under={under} />
      </ScreenFrame>
    );
  }
  return <ScopePageBody scope={scope} screen={screen} period={period} engagement={under} />;
}

/** Wrapped, because there is no unauthenticated surface any more: without the wrapper the page would
 * render its empty state while every query 401s, which looks like a workspace with no data rather
 * than a person who is not signed in. */
export function ScopeTablePage({ scope }: { scope: "1" | "2" }) {
  return (
    <RequireSession>
      <RequirePeriod
        title={`Scope ${scope}`}
        crumb={scope === "1" ? "Direct emissions" : "Purchased electricity"}
        tiles={
          scope === "1"
            ? ["Scope 1 total", "Largest source", "Estimates", "Grade A figures", "Missing bills"]
            : ["Scope 2 total", "Electricity used", "Estimates", "Missing bills"]
        }
      >
        {(screen, period) => <ScopeUnderEngagement scope={scope} screen={screen} period={period} />}
      </RequirePeriod>
    </RequireSession>
  );
}
